import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import type { PayrollPosition, PayrollSettings } from '../types/finances';
import { DEFAULT_PAYROLL_SETTINGS } from '../types/finances';
import { db } from './firebase';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import { mapPayrollPositionFromFirestore, mapPayrollSettingsFromFirestore } from './payrollFirestore';
import type { ContractorProfile, ProposalNodeSet } from '../types';
import { subscribeContractorProfiles } from './contractorTimesheetService';
import {
  buildInstallLaborPeople,
  lastInstallDayRateByUserIdFromNodeSets,
  type InstallLaborPerson,
} from './proposalInstallLabor';
import { canViewTeamCompensation } from './teamCompensationAccess';
import { useAuth } from './AuthContext';
import { useCRM } from './CRMContext';
import { resolveEmployeeDesignations } from './contractorPayablesAccess';

export type UseInstallLaborPeopleOptions = {
  /** Scan install labor lines on these structures for last-used $/day per person. */
  proposalNodeSets?: Pick<ProposalNodeSet, 'install'>[];
};

export function useInstallLaborPeople(options: UseInstallLaborPeopleOptions = {}): InstallLaborPerson[] {
  const { proposalNodeSets } = options;
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const designations = resolveEmployeeDesignations(user, crmUsers);
  const briefingProfile = crmUsers.find((row) => row.uid === user?.id)?.executiveBriefing?.profile
    ?? user?.executiveBriefing?.profile
    ?? null;
  const canViewRates = canViewTeamCompensation({
    authRole: user?.role,
    designations,
    briefingProfile,
  });
  const [positions, setPositions] = useState<PayrollPosition[]>([]);
  const [settings, setSettings] = useState<PayrollSettings>(DEFAULT_PAYROLL_SETTINGS);
  const [contractorProfiles, setContractorProfiles] = useState<Map<string, ContractorProfile>>(new Map());
  const lastInstallDayRateByUserId = useMemo(
    () => lastInstallDayRateByUserIdFromNodeSets(proposalNodeSets),
    [proposalNodeSets],
  );

  useEffect(() => {
    if (!canViewRates) {
      setPositions([]);
      setSettings(DEFAULT_PAYROLL_SETTINGS);
      return;
    }
    const unsub = onSnapshot(
      collection(db, 'payrollPositions'),
      (snap) => {
        setPositions(snap.docs.map((d) => mapPayrollPositionFromFirestore(d.id, d.data() as Record<string, unknown>)));
      },
      (err) => {
        setPositions([]);
        firestoreSnapshotListenError('useInstallLaborPeople', 'payrollPositions', err);
      },
    );
    return unsub;
  }, [canViewRates]);

  useEffect(() => {
    if (!canViewRates) {
      setSettings(DEFAULT_PAYROLL_SETTINGS);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'financeSettings', 'payrollSettings'),
      (snap) => {
        setSettings(mapPayrollSettingsFromFirestore(snap.data() as Record<string, unknown> | undefined));
      },
      (err) => {
        setSettings(DEFAULT_PAYROLL_SETTINGS);
        firestoreSnapshotListenError('useInstallLaborPeople', 'financeSettings/payrollSettings', err);
      },
    );
    return unsub;
  }, [canViewRates]);

  useEffect(() => {
    if (!canViewRates) {
      setContractorProfiles(new Map());
      return;
    }
    const unsub = subscribeContractorProfiles(
      setContractorProfiles,
      (err) => {
        setContractorProfiles(new Map());
        firestoreSnapshotListenError('useInstallLaborPeople', 'contractorProfiles', err);
      },
    );
    return unsub;
  }, [canViewRates]);

  return useMemo(
    () => buildInstallLaborPeople(
      crmUsers,
      canViewRates ? positions : [],
      canViewRates ? settings : DEFAULT_PAYROLL_SETTINGS,
      canViewRates
        ? {
            contractorProfilesByUserId: contractorProfiles,
            lastInstallDayRateByUserId,
          }
        : {},
    ),
    [canViewRates, contractorProfiles, crmUsers, lastInstallDayRateByUserId, positions, settings],
  );
}
